import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query as fsQuery,
  setDoc,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/integrations/firebase/firestore';

// One entry of the per-user search history shown on the empty homepage.
// Stored as a LIGHTWEIGHT mirror at users/{uid}/recent_searches/{docId} —
// never read from drug_searches, whose docs carry the full report payload
// (hundreds of KB) and would make the homepage read prohibitively heavy.
export interface RecentSearch {
  id: string;
  // The committed query exactly as it appears in the /:query URL segment
  // (decoded) — replaying it through goToSearch reattaches the cached report.
  query: string;
  // The human (Latin substance) label shown on the chip, e.g. "Paracetamolum".
  label: string;
  searched_at: string; // ISO timestamp of the LAST completed search
}

// Synchronous first-paint hint: whether this browser has ever seen a non-empty
// history for the last signed-in user. Lets the homepage pick the compact
// (returning-user) vs. full-hero (first-run) layout on the very first render,
// instead of flashing one and swapping when the Firestore snapshot lands.
const HINT_KEY = 'chariot:has-recent-searches';

function readHistoryHint(): boolean {
  try {
    return window.localStorage.getItem(HINT_KEY) === '1';
  } catch {
    return false;
  }
}

function writeHistoryHint(value: boolean): void {
  try {
    window.localStorage.setItem(HINT_KEY, value ? '1' : '0');
  } catch {
    // Private mode / storage disabled — the hint is best-effort only.
  }
}

// Deterministic doc id per query, so a repeat search UPSERTS (bumps
// searched_at) instead of duplicating the entry. encodeURIComponent guarantees
// a Firestore-safe id (no "/", never "." or "..": committed queries are ≥2
// chars and real drug tokens).
function recentSearchDocId(query: string): string {
  return encodeURIComponent(query.trim());
}

// The /:query URL segment is the canonical cache slug — substance names
// sorted and joined with a bare "+" ("Acidum clavulanicum+Amoxicillinum").
// That slug must never surface verbatim as a chip label, so labels pass
// through this normaliser both when written and when rendered ("A+B" →
// "A + B"). Idempotent; a mono-substance label passes through unchanged.
export function humanizeSearchLabel(value: string): string {
  return value
    .split('+')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' + ');
}

// Upsert a history entry. Called when a search reaches "completed" — a failed
// resolution never becomes a recent chip. Best-effort: history must never
// break the report page.
export async function recordRecentSearch(
  uid: string,
  query: string,
  label: string,
): Promise<void> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return;
  try {
    await setDoc(doc(db, 'users', uid, 'recent_searches', recentSearchDocId(trimmedQuery)), {
      query: trimmedQuery,
      label: humanizeSearchLabel(label) || humanizeSearchLabel(trimmedQuery),
      searched_at: new Date().toISOString(),
    });
    writeHistoryHint(true);
  } catch (error) {
    console.error('Failed to record recent search:', error);
  }
}

// Live-subscribe to the newest history entries for the signed-in user. Live
// (not a one-shot read) because Home stays mounted across "/" ⇄ "/:query"
// navigations — returning to the empty homepage must show the search that just
// completed without a remount.
export function useRecentSearches(
  uid: string | null | undefined,
  max = 6,
): { recents: RecentSearch[]; hasHistory: boolean } {
  const [recents, setRecents] = useState<RecentSearch[]>([]);
  const [historyHint, setHistoryHint] = useState(readHistoryHint);

  // Clear on ANY uid change during render (the derived-state pattern): a
  // sign-out must not show the previous user's history, and a sign-in must not
  // pair the new uid with it while the first snapshot is in flight.
  const [trackedUid, setTrackedUid] = useState(uid ?? null);
  if ((uid ?? null) !== trackedUid) {
    setTrackedUid(uid ?? null);
    setRecents([]);
  }

  useEffect(() => {
    if (!uid) return;
    const unsubscribe = onSnapshot(
      fsQuery(
        collection(db, 'users', uid, 'recent_searches'),
        orderBy('searched_at', 'desc'),
        limit(max),
      ),
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<RecentSearch, 'id'>),
        }));
        setRecents(rows);
        setHistoryHint(rows.length > 0);
        writeHistoryHint(rows.length > 0);
      },
      (error) => {
        console.error('Recent searches listener failed:', error);
      },
    );
    return unsubscribe;
  }, [uid, max]);

  return { recents, hasHistory: historyHint || recents.length > 0 };
}
